/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import cx from 'classnames';
import { Button } from '@base-ui/react/button';

import { useUserLocation } from 'shared-ui/geolocation';
import { formatLocationString } from '../../utils/formatLocationString';
import { useFiltersState } from '../../FiltersContext';
import { useSearchResultsQuery } from '../../api/search.queries';
import { ModernIcon } from '../ModernIcon';

import styles from './SearchLocationsControl.module.scss';
import sharedStyles from '../sharedStyles.module.scss';
import { LocationsCombobox } from './LocationsCombobox';

interface SearchLocationsControlProps {
    isSearchExpanded: boolean;
    setIsSearchExpanded: (isSearchExpanded: boolean) => void;
}

export function SearchLocationsControl({
    isSearchExpanded,
    setIsSearchExpanded,
}: SearchLocationsControlProps) {
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const [filters] = useFiltersState();
    const { data, isFetching } = useSearchResultsQuery();
    const userLocation = useUserLocation();

    const collapseAutocomplete = React.useCallback(
        (options?: { shouldFocusTrigger?: boolean }) => {
            setIsSearchExpanded(false);

            if (options?.shouldFocusTrigger) {
                window.setTimeout(() => {
                    buttonRef.current?.focus();
                }, 50);
            }
        },
        [setIsSearchExpanded]
    );

    return (
        <div
            className={cx(styles.searchLocationsControl, {
                [styles.isExpanded]: isSearchExpanded,
            })}
        >
            {isSearchExpanded ? (
                <LocationsCombobox collapseAutocomplete={collapseAutocomplete} />
            ) : (
                <Button
                    className={sharedStyles.triggerButton}
                    onClick={() => setIsSearchExpanded(true)}
                    ref={buttonRef}
                >
                    <ModernIcon iconName="search" size={16} />
                    <span data-truncate-text>
                        {isFetching && filters.location
                            ? formatLocationString(filters.location)
                            : data?.location
                              ? formatLocationString(data.location)
                              : filters.location
                                ? formatLocationString(filters.location)
                                : filters.lat === userLocation?.latitude &&
                                    filters.lng === userLocation?.longitude
                                  ? 'Nearby'
                                  : 'Map area'}
                    </span>
                    <span className="rec-sr-only"> ... Open location picker</span>
                </Button>
            )}
        </div>
    );
}
